import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "typedefs";
import { StripePurchaseModel } from "app/models/scheduling/StripePurchaseModel";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import PendingPurchaseBanner from "../PendingPurchaseBanner";
import styles from "./styles.module.scss";

interface PendingPurchasesListProps {
  filterByPurchasableType?: string;
  filterByCoachId?: string;
  wrapContent?: (content: React.ReactNode) => JSX.Element | null;
}

export default function PendingPurchasesList(props: PendingPurchasesListProps) {
  const { filterByPurchasableType, filterByCoachId, wrapContent } = props;
  const { t } = useTranslation();
  const userType = useSelector((state: RootState) => state.user.data?.kind);
  const [purchases, setPurchases] = useState<StripePurchaseModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPendingPurchases = useCallback(() => {
    if (userType !== "athlete") {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    schedulingFetchGet('/stripe_purchases/pending')
      .then((result) => {
        if (!result || !Array.isArray(result)) {
          setPurchases([]);
          return;
        }

        // Filter out expired purchases and convert to models
        let validPurchases = result
          .map(data => new StripePurchaseModel(data))
          .filter(p => p.isPending && !p.isExpired);

        // Apply purchasableType filter if provided
        if (filterByPurchasableType) {
          validPurchases = validPurchases.filter(
            p => p.purchasableType === filterByPurchasableType
          );
        }

        // Apply coachId filter if provided
        if (filterByCoachId) {
          validPurchases = validPurchases.filter(
            p => p.coachProfileId === filterByCoachId
          );
        }

        setPurchases(validPurchases);
      })
      .catch((err) => {
        if (err.status !== 403) {
          console.error('Error fetching pending purchases:', err);
          setError(t("Failed to load pending purchases"));
        }
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, filterByPurchasableType, filterByCoachId]);

  useEffect(() => {
    fetchPendingPurchases();
  }, [fetchPendingPurchases]);

  const handlePurchaseCancelled = () => {
    // Refetch to update the list
    fetchPendingPurchases();
  };

  if (userType !== "athlete") {
    return null;
  }

  if (loading) {
    return null;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (purchases.length === 0) {
    return null;
  }

  const content = (
    <div className={styles.container}>
      {purchases.map((purchase) => (
        <PendingPurchaseBanner
          key={purchase.id}
          purchase={purchase}
          onCancelled={handlePurchaseCancelled}
        />
      ))}
    </div>
  );

  return wrapContent ? wrapContent(content) : content;
}
